import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Database, Loader2, Plus } from 'lucide-react';

/**
 * Two-step "Create Semantic Model" dialog.
 * Step 1 — pick a data source from the list
 * Step 2 — name + description, then call onSubmit()
 *
 * Parent owns all state (step, sourceId/Name, name, desc, saving) so the
 * confirmation handler can live next to its API call. This mirrors how
 * the DataPrep dialogs are structured.
 */
export function CreateModelDialog({
  open, onOpenChange,
  step, setStep,
  sources,
  sourceId, setSource, // setSource(id, name)
  name, setName,
  desc, setDesc,
  saving, onSubmit,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-card border-border/50 rounded-2xl sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading">
            {step === 1 ? 'Select Data Source' : 'Create Semantic Model'}
          </DialogTitle>
        </DialogHeader>

        {step === 1 ? (
          <div className="space-y-3 mt-2">
            <p className="text-xs text-muted-foreground">Choose the data source this model will be built against</p>
            <div className="space-y-2 max-h-72 overflow-auto" data-testid="source-picker">
              {sources.map(src => (
                <button
                  key={src.source_id}
                  onClick={() => { setSource(src.source_id, src.name); setStep(2); }}
                  className={`w-full flex items-center gap-3 p-3.5 rounded-xl border text-left transition-all active:scale-[0.98] hover:border-primary/30 hover:bg-primary/5 ${sourceId === src.source_id ? 'border-primary/40 bg-primary/5' : 'border-border/30'}`}
                  data-testid={`pick-source-${src.source_id}`}
                >
                  <div className="p-2 rounded-lg bg-blue-500/10 border border-blue-500/20">
                    <Database className="w-5 h-5 text-blue-400" strokeWidth={1.5} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-heading font-semibold text-foreground">{src.name}</p>
                    <p className="text-[10px] text-muted-foreground truncate">{src.description || src.source_type}</p>
                  </div>
                  <div className="flex flex-col items-end gap-1 flex-shrink-0">
                    <Badge variant="outline" className="text-[9px]">{src.source_type}</Badge>
                    <Badge className={`text-[9px] ${src.status === 'connected' ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/20' : 'bg-amber-500/15 text-amber-400 border-amber-500/20'}`}>{src.status}</Badge>
                  </div>
                </button>
              ))}
              {sources.length === 0 && (
                <div className="p-6 text-center text-xs text-muted-foreground">No data sources available. Add one first.</div>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-3 mt-2">
            <div className="flex items-center gap-2 p-2.5 rounded-lg bg-muted/20 border border-border/30">
              <Database className="w-4 h-4 text-blue-400" />
              <div className="flex-1">
                <p className="text-xs font-medium text-foreground">{sources.find(s => s.source_id === sourceId)?.name || ''}</p>
                <p className="text-[9px] text-muted-foreground">Selected data source</p>
              </div>
              <button onClick={() => setStep(1)} className="text-[10px] text-primary hover:underline">Change</button>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Model Name</Label>
              <Input data-testid="model-name-input" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Claims Analytics Model" className="rounded-lg bg-muted/50 border-border/50 mt-1" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Description</Label>
              <Input value={desc} onChange={e => setDesc(e.target.value)} placeholder="What this model covers" className="rounded-lg bg-muted/50 border-border/50 mt-1" />
            </div>
            <Button onClick={onSubmit} disabled={saving || !name.trim()} className="w-full rounded-lg bg-primary text-primary-foreground active:scale-95" data-testid="confirm-create-model">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />} Create Model
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
